import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingLayout from '../components/OnboardingLayout';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import { Button } from '../components/ui/button';
import { Loader2, Clock, CalendarDays, Compass } from 'lucide-react';
import { toast } from 'sonner';

const OPTIONS = [
  { value: 'within_30_days', label: 'Within 30 days', icon: Clock },
  { value: '1_to_3_months', label: 'In 1 to 3 months', icon: CalendarDays },
  { value: '3_plus', label: '3+ months, or just exploring', icon: Compass },
];

const OnboardingMoveIn = () => {
  const navigate = useNavigate();
  const { user, loading, updateOnboarding } = useAuth();
  const [selected, setSelected] = useState(user?.move_in_urgency || null);
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  if (!user) {
    navigate('/signup', { replace: true });
    return null;
  }

  const saveAndContinue = async () => {
    if (!selected) {
      navigate('/onboarding/budget');
      return;
    }
    setSaving(true);
    try {
      await updateOnboarding({ move_in_urgency: selected });
      navigate('/onboarding/budget');
    } catch (e) {
      toast.error("We couldn't save that. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <VerifyEmailBanner />
      <OnboardingLayout
        step={2}
        title="When do you need to move in?"
        testId="onboarding-move-in"
      >
        <div className="space-y-3">
          {OPTIONS.map((opt) => {
            const active = selected === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left p-5 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                  active
                    ? 'border-amber-500 bg-amber-50/60 shadow-soft'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`move-in-option-${opt.value}`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    active ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="font-medium text-slate-900 text-base">{opt.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 space-y-3">
          <Button
            onClick={saveAndContinue}
            disabled={saving}
            className="w-full h-12 btn-primary text-base"
            data-testid="move-in-continue-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
          <button
            onClick={() => navigate('/onboarding/budget')}
            className="w-full text-sm text-slate-600 hover:text-slate-900 py-2"
            data-testid="move-in-skip-link"
          >
            Skip this step
          </button>
        </div>
      </OnboardingLayout>
    </>
  );
};

export default OnboardingMoveIn;
